import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Email from '@mui/icons-material/Email';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {
  ACCOUNT_COLORS,
  accountDialogFieldLabelSx,
  accountFieldSx,
  accountPhoneInputStyles,
  accountReadOnlyBoxSx,
} from './accountPageStyles';
import { AccountButton } from './AccountButton';
import type { ClientCountryOption, ClientStateOption } from '../../utils/clientProfileDisplay';

export type AccountSettingsDialog = 'name' | 'phone' | 'location' | 'security';

interface AccountSettingsDialogsProps {
  openDialog: AccountSettingsDialog | null;
  onCloseDialog: () => void;
  clientEmail?: string;
  name: string;
  hasGoogle?: boolean;
  hasPassword?: boolean;
  nameLoading: boolean;
  nameError: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  showCurrentPassword: boolean;
  showNewPassword: boolean;
  showConfirmPassword: boolean;
  passwordLoading: boolean;
  passwordError: string;
  onNameChange: (value: string) => void;
  onSaveName: () => Promise<boolean> | boolean;
  phone: string;
  phoneLoading: boolean;
  phoneError: string;
  countries: ClientCountryOption[];
  states: ClientStateOption[];
  country: ClientCountryOption | null;
  state: ClientStateOption | null;
  locationLoading: boolean;
  locationError: string;
  onPhoneChange: (value: string) => void;
  onSavePhone: () => Promise<boolean> | boolean;
  onCountryChange: (value: ClientCountryOption | null) => void;
  onStateChange: (value: ClientStateOption | null) => void;
  onSaveLocation: () => Promise<boolean> | boolean;
  onClearPasswordError: () => void;
  onCurrentPasswordChange: (value: string) => void;
  onNewPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onToggleCurrentPassword: () => void;
  onToggleNewPassword: () => void;
  onToggleConfirmPassword: () => void;
  onSubmitPassword: () => Promise<void> | void;
}

const passwordToggle = (show: boolean, toggle: () => void) => (
  <InputAdornment position="end">
    <IconButton onClick={toggle} edge="end" size="small" aria-label="Toggle password visibility">
      {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
    </IconButton>
  </InputAdornment>
);

export const AccountSettingsDialogs: React.FC<AccountSettingsDialogsProps> = ({
  openDialog,
  onCloseDialog,
  clientEmail,
  name,
  hasGoogle,
  hasPassword,
  nameLoading,
  nameError,
  currentPassword,
  newPassword,
  confirmPassword,
  showCurrentPassword,
  showNewPassword,
  showConfirmPassword,
  passwordLoading,
  passwordError,
  onNameChange,
  onSaveName,
  phone,
  phoneLoading,
  phoneError,
  countries,
  states,
  country,
  state,
  locationLoading,
  locationError,
  onPhoneChange,
  onSavePhone,
  onCountryChange,
  onStateChange,
  onSaveLocation,
  onClearPasswordError,
  onCurrentPasswordChange,
  onNewPasswordChange,
  onConfirmPasswordChange,
  onToggleCurrentPassword,
  onToggleNewPassword,
  onToggleConfirmPassword,
  onSubmitPassword,
}) => {
  const handleSaveName = async () => {
    const success = await onSaveName();
    if (success) onCloseDialog();
  };

  const handleSavePhone = async () => {
    const success = await onSavePhone();
    if (success) onCloseDialog();
  };

  const handleSaveLocation = async () => {
    const success = await onSaveLocation();
    if (success) onCloseDialog();
  };

  return (
    <>
      <Dialog
        open={openDialog === 'name'}
        onClose={onCloseDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: ACCOUNT_COLORS.radius } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          Display name
          <IconButton size="small" onClick={onCloseDialog} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={accountDialogFieldLabelSx}>Display name</Typography>
          <TextField
            fullWidth
            size="small"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            error={Boolean(nameError)}
            helperText={nameError || 'How your name appears to our team.'}
            placeholder="Your display name"
            sx={accountFieldSx}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <AccountButton accountVariant="outline" onClick={onCloseDialog}>
              Cancel
            </AccountButton>
            <AccountButton accountVariant="gold" loading={nameLoading} onClick={() => void handleSaveName()}>
              Save
            </AccountButton>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDialog === 'phone'}
        onClose={onCloseDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: ACCOUNT_COLORS.radius } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          Phone number
          <IconButton size="small" onClick={onCloseDialog} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={accountDialogFieldLabelSx}>Phone</Typography>
          <PhoneInput
            country={country?.isoCode.toLowerCase() ?? 'ca'}
            value={phone}
            onChange={onPhoneChange}
            enableSearch
            searchPlaceholder="Search country…"
            {...accountPhoneInputStyles}
          />
          {phoneError && (
            <Typography sx={{ mt: 1, fontSize: '0.75rem', color: ACCOUNT_COLORS.danger }}>
              {phoneError}
            </Typography>
          )}
          <Typography sx={{ mt: 1, fontSize: '0.75rem', color: ACCOUNT_COLORS.textMuted }}>
            Used for appointment reminders and important updates.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <AccountButton accountVariant="outline" onClick={onCloseDialog}>
              Cancel
            </AccountButton>
            <AccountButton accountVariant="gold" loading={phoneLoading} onClick={() => void handleSavePhone()}>
              Save
            </AccountButton>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDialog === 'location'}
        onClose={onCloseDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: ACCOUNT_COLORS.radius } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          Location
          <IconButton size="small" onClick={onCloseDialog} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={accountDialogFieldLabelSx}>Country</Typography>
          <Autocomplete
            options={countries}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.isoCode === value.isoCode}
            value={country}
            onChange={(_, newValue) => {
              onCountryChange(newValue);
              onStateChange(null);
            }}
            renderInput={(params) => (
              <TextField {...params} size="small" placeholder="Select country" sx={accountFieldSx} />
            )}
            sx={{ mb: 2 }}
          />

          <Typography sx={accountDialogFieldLabelSx}>Province / State</Typography>
          <Autocomplete
            options={states}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.isoCode === value.isoCode}
            value={state}
            onChange={(_, newValue) => onStateChange(newValue)}
            disabled={!country}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder={country ? 'Select province or state' : 'Select country first'}
                sx={accountFieldSx}
              />
            )}
          />
          {locationError && (
            <Typography sx={{ mt: 1, fontSize: '0.75rem', color: ACCOUNT_COLORS.danger }}>
              {locationError}
            </Typography>
          )}
          <Typography sx={{ mt: 1, fontSize: '0.75rem', color: ACCOUNT_COLORS.textMuted }}>
            Helps us show local services and time zones correctly.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <AccountButton accountVariant="outline" onClick={onCloseDialog}>
              Cancel
            </AccountButton>
            <AccountButton accountVariant="gold" loading={locationLoading} onClick={() => void handleSaveLocation()}>
              Save
            </AccountButton>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDialog === 'security'}
        onClose={onCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: ACCOUNT_COLORS.radius } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          Sign-in & security
          <IconButton size="small" onClick={onCloseDialog} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Email sx={{ fontSize: 18, color: ACCOUNT_COLORS.gold }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Email</Typography>
            </Stack>
            <Box sx={accountReadOnlyBoxSx}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{clientEmail || 'Not set'}</Typography>
                {hasGoogle && <Chip label="Google" size="small" sx={{ bgcolor: ACCOUNT_COLORS.surface3, fontWeight: 600 }} />}
              </Stack>
            </Box>
          </Box>

          {!hasPassword && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: ACCOUNT_COLORS.radiusSm }}>
              You signed in with Google. Set a password to also use email sign-in.
            </Alert>
          )}

          <Box component="form" onSubmit={(e) => { e.preventDefault(); void onSubmitPassword(); }}>
            {passwordError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: ACCOUNT_COLORS.radiusSm }} onClose={onClearPasswordError}>
                {passwordError}
              </Alert>
            )}
            {hasPassword && (
              <TextField
                fullWidth
                label="Current password"
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => onCurrentPasswordChange(e.target.value)}
                margin="dense"
                size="small"
                sx={accountFieldSx}
                InputProps={{ endAdornment: passwordToggle(showCurrentPassword, onToggleCurrentPassword) }}
              />
            )}
            <TextField
              fullWidth
              label="New password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => onNewPasswordChange(e.target.value)}
              margin="dense"
              size="small"
              helperText="Minimum 6 characters"
              sx={accountFieldSx}
              InputProps={{ endAdornment: passwordToggle(showNewPassword, onToggleNewPassword) }}
            />
            <TextField
              fullWidth
              label="Confirm new password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => onConfirmPasswordChange(e.target.value)}
              margin="dense"
              size="small"
              sx={accountFieldSx}
              InputProps={{ endAdornment: passwordToggle(showConfirmPassword, onToggleConfirmPassword) }}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
              <AccountButton accountVariant="outline" onClick={onCloseDialog}>
                Cancel
              </AccountButton>
              <AccountButton type="submit" accountVariant="gold" loading={passwordLoading}>
                {hasPassword ? 'Update password' : 'Set password'}
              </AccountButton>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
